import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { collateralApi, ApiError } from '../services/collateralApi';
import type { MachineryListItem } from '../types/Machinery';

export default function useMachineryPageList() {
    const navigate = useNavigate();
    const [refreshKey, setRefreshKey] = useState(0);
    const [showNewForm, setShowNewForm] = useState(false);
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [sortField, setSortField] = useState('updatedAt');
    const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('DESC');
    const [machineryType, setMachineryType] = useState('');
    const [archived, setArchived] = useState('false');
    const [rows, setRows] = useState<MachineryListItem[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const pageSize = 100;
    const [isLoading, setIsLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);
    const [filterCounts, setFilterCounts] = useState<Record<string, any>>({});
    const [apiInfo, setApiInfo] = useState({ version: '', environment: '' });

    // Side panel
    const [selectedMachinery, setSelectedMachinery] = useState<any>(null);
    const [machineryDetail, setMachineryDetail] = useState<any>(null);
    const [isLoadingDetail, setIsLoadingDetail] = useState(false);

    const requestSeq = useRef(0);

    const fetchData = useCallback(async () => {
        const requestId = ++requestSeq.current;
        setIsLoading(true);
        try {
            const result = await collateralApi.getMachineries({
                page,
                pageSize,
                search: search || undefined,
                machineryType: machineryType || undefined,
                archived: archived || undefined,
                sortField: sortField || undefined,
                sortOrder: sortOrder || undefined,
            });
            if (requestId !== requestSeq.current) return;
            setRows(result.items || []);
            setTotal(result.total || 0);
            setFilterCounts(result.filterCounts || {});
            setApiInfo({ version: result.apiVersion || '', environment: result.environment || '' });
        } catch (e: any) {
            if (requestId !== requestSeq.current) return;
            if (e instanceof ApiError) {
                if (e.status === 403) return navigate('/denied');
                if (e.status === 404) return navigate('/not-found');
            }
            navigate('/error');
        } finally {
            if (requestId === requestSeq.current) {
                setIsLoading(false);
                setInitialLoading(false);
            }
        }
    }, [page, search, machineryType, archived, sortField, sortOrder, navigate]);

    useEffect(() => { fetchData(); }, [fetchData, refreshKey]);

    const refresh = () => setRefreshKey(k => k + 1);

    const handleRowClick = async (row: MachineryListItem) => {
        setSelectedMachinery(row);
        setIsLoadingDetail(true);
        try {
            const detail = await collateralApi.getMachineryView(row.id);
            setMachineryDetail(detail);
        } catch {
            setMachineryDetail(null);
        } finally {
            setIsLoadingDetail(false);
        }
    };

    const closePanel = () => {
        setSelectedMachinery(null);
        setMachineryDetail(null);
    };

    return {
        rows, total, page, setPage, pageSize,
        isLoading, initialLoading, filterCounts,
        showNewForm, setShowNewForm,
        searchInput, setSearchInput, search, setSearch,
        sortField, setSortField, sortOrder, setSortOrder,
        machineryType, setMachineryType,
        archived, setArchived,
        refresh, handleRowClick,
        selectedMachinery, machineryDetail, isLoadingDetail, closePanel,
        apiInfo,
    };
}
